import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    fileText: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    pieChart: 'M21.21 15.89A10 10 0 1 1 8 2.83 M22 12A10 10 0 0 0 12 2v10z',
    dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    calendar: 'M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z M16 2v4 M8 2v4 M3 10h18',
    activity: 'M22 12h-4l-3 9L9 3l-3 9H2'
};

export default function MaintenanceReports({ user, operatorDetails }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        fetchJobs();
    }, [user]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const opId = user?.operatorId || user?._id || 'all';
            const res = await axios.get(`${API}/maintenance-jobs/${opId}`);
            setJobs(res.data);
        } catch (err) {
            console.error('Fetch reports error:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredJobs = jobs.filter(job => {
        const jobDate = job.maintenanceDate ? new Date(job.maintenanceDate).toISOString().split('T')[0] : '';
        if (startDate && jobDate < startDate) return false;
        if (endDate && jobDate > endDate) return false;
        return true;
    });

    const totalCost = filteredJobs.reduce((sum, job) => sum + (job.estimatedCost || 0), 0);
    const jobCount = filteredJobs.length;
    const completedJobs = filteredJobs.filter(j => j.status?.toLowerCase().includes('complete') || j.status?.toLowerCase().includes('done')).length;
    const pendingJobs = jobCount - completedJobs;

    const handleExport = () => {
        alert('Exporting report as CSV...');
        // In a real app, this would generate and download a CSV
    };

    return (
        <div className="mr-page">
            <style>{`
                .mr-page { padding: 40px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .mr-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; }
                .mr-title-group h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .mr-title-group p { font-size: 14px; color: #64748b; margin: 4px 0 0; }

                .mr-export-btn {
                    display: flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 12px;
                    background: white; border: 1.5px solid #e2e8f0; color: #1e293b; font-weight: 700;
                    cursor: pointer; transition: all 0.2s; font-size: 14px;
                }
                .mr-export-btn:hover { background: #f8fafc; border-color: #cbd5e1; transform: translateY(-1px); }

                .mr-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; margin-bottom: 32px; }
                .mr-stat-card { 
                    background: white; padding: 24px; border-radius: 20px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); display: flex; flex-direction: column; gap: 12px;
                }
                .mr-stat-icon { 
                    width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; 
                    justify-content: center; background: #f8fafc;
                }
                .mr-stat-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                .mr-stat-value { font-size: 24px; font-weight: 800; color: #1e293b; }

                .mr-filters { 
                    display: flex; gap: 20px; background: white; padding: 24px; border-radius: 20px; 
                    border: 1px solid #e2e8f0; margin-bottom: 32px; align-items: flex-end;
                }
                .mr-filter-group { display: flex; flex-direction: column; gap: 8px; flex: 1; }
                .mr-filter-label { font-size: 12px; font-weight: 700; color: #475569; }
                .mr-input { 
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s;
                }
                .mr-input:focus { border-color: #6366f1; }

                .mr-report-panel { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; }
                .mr-table { width: 100%; border-collapse: collapse; }
                .mr-table th { 
                    background: #f8fafc; padding: 16px 24px; text-align: left; font-size: 12px; 
                    font-weight: 700; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #f1f5f9;
                }
                .mr-table td { padding: 18px 24px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                .mr-table tr:last-child td { border-bottom: none; }
                .mr-table tr:hover { background: #fafbfc; }

                .mr-badge { 
                    padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 700; 
                    text-transform: uppercase; display: inline-block;
                }
                .badge-complete { background: #dcfce7; color: #15803d; }
                .badge-pending { background: #fef3c7; color: #b45309; }
                .badge-general { background: #f1f5f9; color: #475569; }
            `}</style>

            <header className="mr-header">
                <div className="mr-title-group">
                    <h1>Fleet Maintenance Reports</h1>
                    <p>Comprehensive analysis of vehicle maintenance activities and costs.</p>
                </div>
                <button className="mr-export-btn" onClick={handleExport}>
                    <Icon d={ic.download} size={18} /> Export CSV
                </button>
            </header>

            <div className="mr-filters">
                <div className="mr-filter-group">
                    <label className="mr-filter-label">Report From Date</label>
                    <input type="date" className="mr-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="mr-filter-group">
                    <label className="mr-filter-label">Report To Date</label>
                    <input type="date" className="mr-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>{/* Spacer */}</div>
            </div>

            <div className="mr-stats-grid">
                <div className="mr-stat-card">
                    <div className="mr-stat-icon" style={{ background: '#eef2ff', color: '#6366f1' }}>
                        <Icon d={ic.dollar} size={20} />
                    </div>
                    <span className="mr-stat-label">Total Expenditure</span>
                    <span className="mr-stat-value">{formatCurrency(totalCost, operatorDetails?.country)}</span>
                </div>
                <div className="mr-stat-card">
                    <div className="mr-stat-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                        <Icon d={ic.activity} size={20} />
                    </div>
                    <span className="mr-stat-label">Total Jobs Logged</span>
                    <span className="mr-stat-value">{jobCount}</span>
                </div>
                <div className="mr-stat-card">
                    <div className="mr-stat-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
                        <Icon d={ic.fileText} size={20} />
                    </div>
                    <span className="mr-stat-label">Completed Jobs</span>
                    <span className="mr-stat-value">{completedJobs}</span>
                </div>
                <div className="mr-stat-card">
                    <div className="mr-stat-icon" style={{ background: '#fbcfe8', color: '#be185d' }}>
                        <Icon d={ic.pieChart} size={20} />
                    </div>
                    <span className="mr-stat-label">Pending / In Progress</span>
                    <span className="mr-stat-value">{pendingJobs}</span>
                </div>
            </div>

            <div className="mr-report-panel">
                <table className="mr-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Job Description</th>
                            <th>Service Category</th>
                            <th>Workshop / Vendor</th>
                            <th>Maintenance Cost</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Generating report data...</td></tr>
                        ) : filteredJobs.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>No maintenance records found for the selected period.</td></tr>
                        ) : (
                            filteredJobs.map(job => (
                                <tr key={job._id}>
                                    <td style={{ fontWeight: 600 }}>{new Date(job.maintenanceDate).toLocaleDateString()}</td>
                                    <td>{job.jobName}</td>
                                    <td>{job.serviceType}</td>
                                    <td>{job.workshopId?.name || 'In-house / Unknown'}</td>
                                    <td style={{ fontWeight: 700, color: '#1e293b' }}>{formatCurrency(job.estimatedCost, operatorDetails?.country)}</td>
                                    <td>
                                        <span className={`mr-badge ${job.status?.toLowerCase().includes('complete') ? 'badge-complete' :
                                                job.status?.toLowerCase().includes('pending') ? 'badge-pending' : 'badge-general'
                                            }`}>
                                            {job.status}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
